import { spawn } from 'node:child_process'
import type { RunMode, RunVercelResult, VercelRunner } from '@cli-types/next-adapter/vercel.js'
import { DEFAULT_CAPTURE_TIMEOUT_MS } from '@cli-constants/timeouts.js'
import { stripAnsi } from '@cli-utils/text.js'
import { createFilteredLineForwarder } from './create-filtered-line-forwarder.js'

export function runVercel(
  runner: VercelRunner,
  args: string[],
  options: {
    cwd: string
    mode: RunMode
    timeoutMs?: number
    env?: NodeJS.ProcessEnv
    streamLineFilter?: (line: string) => boolean
    stdinInput?: string
    onOutputLine?: (line: string, run: { extendTimeout: (ms: number) => void }) => void
  }
): Promise<RunVercelResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_CAPTURE_TIMEOUT_MS
  const argv = [...runner.prefix, ...args]

  return new Promise((resolve) => {
    const child = spawn(runner.bin, argv, {
      cwd: options.cwd,
      stdio:
        options.mode === 'inherit'
          ? 'inherit'
          : options.mode === 'stream'
            ? ['inherit', 'pipe', 'pipe']
            : 'pipe',
      // npm_config_loglevel silences npx's "npm warn deprecated" install noise
      // on the npx runner; harmless for a PATH-installed vercel.
      env: { ...process.env, npm_config_loglevel: 'error', ...options.env }
    })

    let stdout = ''
    let stderr = ''
    let timedOut = false
    let closed = false
    let effectiveTimeoutMs = timeoutMs
    let timeout: NodeJS.Timeout | undefined
    const armTimeout = (ms: number) => {
      if (closed) return
      clearTimeout(timeout)
      effectiveTimeoutMs = ms
      timeout = setTimeout(() => {
        timedOut = true
        child.kill('SIGTERM')
      }, ms)
    }
    const runControl = { extendTimeout: armTimeout }
    const onOutputLine = options.onOutputLine
    const createOutputObserver = () => {
      if (!onOutputLine) return undefined
      return createFilteredLineForwarder(
        (line) => line.length > 0,
        (text) => onOutputLine(stripAnsi(text).trim(), runControl)
      )
    }
    const stdoutObserver = createOutputObserver()
    const stderrObserver = createOutputObserver()

    if (options.mode === 'capture' && options.stdinInput !== undefined) {
      child.stdin?.write(options.stdinInput)
      child.stdin?.end()
    }

    if (options.mode === 'capture') {
      child.stdout?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stdout += text
        stdoutObserver?.push(text)
      })
      child.stderr?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stderr += text
        stderrObserver?.push(text)
      })
    } else if (options.mode === 'stream') {
      const shouldShow = options.streamLineFilter ?? (() => true)
      const outForwarder = createFilteredLineForwarder(shouldShow, (text) =>
        process.stdout.write(text)
      )
      const errForwarder = createFilteredLineForwarder(shouldShow, (text) =>
        process.stderr.write(text)
      )
      child.stdout?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stdout += text
        outForwarder.push(text)
        stdoutObserver?.push(text)
      })
      child.stderr?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stderr += text
        errForwarder.push(text)
        stderrObserver?.push(text)
      })
      child.on('close', () => {
        outForwarder.flush()
        errForwarder.flush()
      })
    }

    armTimeout(timeoutMs)

    child.on('close', (code, signal) => {
      closed = true
      clearTimeout(timeout)
      stdoutObserver?.flush()
      stderrObserver?.flush()

      if (timedOut) {
        resolve({
          success: false,
          code,
          signal,
          stdout,
          stderr,
          timedOut: true,
          errorMessage: `Vercel command timed out after ${Math.floor(effectiveTimeoutMs / 1000)}s`
        })
        return
      }

      resolve({
        success: code === 0,
        code,
        signal,
        stdout,
        stderr,
        timedOut: false,
        errorMessage:
          code === 0
            ? undefined
            : signal
              ? `Vercel command terminated by ${signal}`
              : `Vercel command exited with code ${code ?? 'unknown'}`
      })
    })

    child.on('error', (err) => {
      closed = true
      clearTimeout(timeout)
      stdoutObserver?.flush()
      stderrObserver?.flush()
      resolve({
        success: false,
        code: null,
        signal: null,
        stdout,
        stderr,
        timedOut: false,
        errorMessage: err.message
      })
    })
  })
}
